import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import MobileLayout from "@/components/MobileLayout";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { analytics, appPlatform, flushAnalytics, type SignupProvider } from "@/lib/analytics";
import { metaIdentify, metaTrack, sendMetaCapi, signupEventId } from "@/lib/metaPixel";
import { isInAppBrowser } from "@/lib/isInAppBrowser";
import { claimReferral, clearStoredReferralCode, readStoredReferralCode, normalizeReferralCode } from "@/lib/referral";
import { signupMetadata } from "@/lib/attribution";
import { APP_VERSION } from "@/lib/appVersion";
import { exchangeNativeOAuthOnce } from "@/lib/oauthExchange";
import { toast } from "sonner";
import { ArrowLeft, PlayCircle, Apple, Loader2, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useAppSplash } from "@/contexts/AppSplashContext";
import { Sentry } from "@/lib/sentry";
import {
  RESEND_COOLDOWN_MS,
  authCallbackRedirectTo,
  decideSignupOutcome,
  writePendingConfirmation,
} from "@/lib/emailConfirmation";

// iOS native OAuth — bundle ID is also the Apple Sign In `clientId` audience
// the identity token carries. Must match com.apple.developer.applesignin
// entitlement target. Keep in sync with capacitor.config.ts `appId`.
const NATIVE_BUNDLE_ID = "com.aivyv.mirror";
// Custom URL scheme registered in ios/App/App/Info.plist (CFBundleURLTypes).
// Provider redirects here after Google OAuth; the appUrlOpen listener in
// App.tsx exchanges the code for a session.
const NATIVE_OAUTH_REDIRECT = "com.aivyv.mirror://auth/callback";

// Google OAuth client IDs. These are PUBLIC identifiers (not secrets) — safe to
// hardcode, and hardcoding avoids a reset --hard wiping a local .env on the
// build Mac. iOS client ID = the native GIDSignIn audience; Web client ID =
// the serverClientId/audience the Supabase Google provider validates against
// (the iOS client ID must also be added to the provider's Authorized Client IDs).
const GOOGLE_IOS_CLIENT_ID =
  "67110954203-tt9smdr466ntkui9a4u6bqhqj4hcilsm.apps.googleusercontent.com";
const GOOGLE_WEB_CLIENT_ID =
  "67110954203-2l7odsuc8887itqrjtvngnorrh0csg9m.apps.googleusercontent.com";

// Apple requires the nonce SENT to authorize() to be the SHA-256 hash of the
// raw value, and the nonce sent to Supabase to be the RAW value. Supabase
// hashes server-side and compares against the `nonce` claim in identityToken.
async function generateAppleNonce(): Promise<{ rawNonce: string; hashedNonce: string }> {
  const buf = new Uint8Array(32);
  crypto.getRandomValues(buf);
  const rawNonce = Array.from(buf).map((b) => b.toString(16).padStart(2, "0")).join("");
  const hashBuf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(rawNonce));
  const hashedNonce = Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return { rawNonce, hashedNonce };
}

interface AuthProps {
  onShowIntro?: () => void;
}

// Canonical Privacy Policy URL (self-hosted — see src/pages/Legal/).
const PRIVACY_POLICY_URL =
  typeof window !== "undefined"
    ? `${window.location.origin}/privacy`
    : "/privacy";

// Policy revision pinned into each consent row. Bump this when the policy
// text materially changes so re-consent can be demanded per LGPD Art. 8 §5.
const PRIVACY_POLICY_VERSION = "v1.0";

// Pre-launch whitelist gate (migration beta_whitelist_lockdown_pre_launch
// 06/05). When true: signup CTA is hidden, page defaults to sign-in mode,
// and the catch block translates the trigger's `beta_closed: ...` error
// into a user-friendly toast. Flip to false on launch 15/06 once the
// auth.users BEFORE INSERT trigger is dropped.
const BETA_WHITELIST_MODE = false;

// NOTE: a prewarmMirrorEdgeFunctions() helper used to fire 3 invalid POSTs to
// mirror-chat / mirror-stt / mirror-tts on every sign-in. All three returned
// 400 (the warmup payload matches no function's schema) — pure Sentry +
// console noise with zero warm-up benefit, behind a silent catch. The
// AuthCallback copy was removed in Plans V3.2 Phase 1 for exactly this
// reason; this one is now gone too (12/06).

const Auth = ({ onShowIntro }: AuthProps = {}) => {
  const navigate = useNavigate();
  // Public acquisition keeps signup as its default; account gates explicitly
  // request signin. The URL stays aligned with manual toggles and browser history.
  const [searchParams, setSearchParams] = useSearchParams();
  const isSignUp = !BETA_WHITELIST_MODE && searchParams.get("mode") !== "signin";
  const setIsSignUp = (signUp: boolean) => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set("mode", signUp ? "signup" : "signin");
      return next;
    }, { replace: true });
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<"google" | "apple" | null>(null);
  // Forgot-password modal state. Opens from the "forgot password" link
  // below the password input on sign-in mode only. Submits via
  // supabase.auth.resetPasswordForEmail; the user finishes the flow on
  // /reset-password after clicking the email link.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  // Existing-account dedupe (Sentry VYV-FRONTEND-2M): a signup submit with an
  // email that already has an account first tries a silent sign-in; when that
  // fails we flip to sign-in mode and show this persistent inline notice — a
  // toast would vanish before the user understands why the form changed.
  const [dedupeNotice, setDedupeNotice] = useState(false);
  // Referral code — prefilled from a captured aivyv.com/r/{code} link, also
  // editable by hand. Optional; never blocks signup.
  const [referralCode, setReferralCode] = useState(() => readStoredReferralCode() ?? "");
  const { t, tf } = useLanguage();
  const splash = useAppSplash();

  // ── Funnel: signup_started ──────────────────────────────────────────────────
  // Fired the first time the visitor engages a signup path, tagged with the
  // method that triggered it. Two entry points:
  //   • email/password form — on first keystroke (onChange), gated to signup
  //     mode at the call site so sign-in form-fills aren't counted as signups.
  //   • social buttons — on click of Google / Apple (see handleOAuth). OAuth is
  //     mode-agnostic (Supabase creates the account if new), so it is NOT gated
  //     on isSignUp — otherwise social signups would never fire the event and
  //     the funnel would show completed > started for that segment.
  // Previously signup_started fired only at email submit, a near-clone of
  // signup_completed that left the form-fill drop-off (the 1,429 → 18 leak)
  // invisible AND missed social entirely. Guarded to fire at most once per
  // mount; re-armed on the sign-in/up toggle so each fresh intent counts once.
  // Per-provider guard. A visitor who touches the email field (arming "email")
  // and THEN clicks Google must still fire signup_started for google — the old
  // single boolean suppressed the second path, so most OAuth signups went
  // uncounted (prod: google started 11 << completed 24). Fires each provider at
  // most once per mount; re-armed on the sign-in/up toggle below.
  const signupStartedProvidersRef = useRef<Set<SignupProvider>>(new Set());
  const markSignupStarted = useCallback(
    (provider: SignupProvider, options?: Parameters<typeof analytics.signupStarted>[1]) => {
      if (signupStartedProvidersRef.current.has(provider)) return;
      signupStartedProvidersRef.current.add(provider);
      analytics.signupStarted(provider, options);
    },
    [],
  );

  // ── Native Google OAuth recovery ───────────────────────────────────────────
  // The Google path opens an SFSafariViewController and waits for the
  // `appUrlOpen` deep link (handled in App.tsx) to finish the handshake. If that
  // callback never arrives — the user dismisses the sheet, or the provider /
  // redirect never returns — `oauthLoading` would otherwise stick on forever
  // (frozen spinner, no recovery). These refs back a watchdog + dismiss listener
  // that un-stick the UI. The Apple path is fully native and never uses them.
  const mountedRef = useRef(true);
  const oauthActiveRef = useRef(false);
  const oauthWatchdogRef = useRef<number | null>(null);
  const oauthDismissTimerRef = useRef<number | null>(null);
  const browserFinishedRef = useRef<{ remove: () => void } | null>(null);

  const clearOAuthRecovery = useCallback(() => {
    oauthActiveRef.current = false;
    if (oauthWatchdogRef.current !== null) {
      window.clearTimeout(oauthWatchdogRef.current);
      oauthWatchdogRef.current = null;
    }
    if (oauthDismissTimerRef.current !== null) {
      window.clearTimeout(oauthDismissTimerRef.current);
      oauthDismissTimerRef.current = null;
    }
    if (browserFinishedRef.current) {
      try { browserFinishedRef.current.remove(); } catch { /* listener already gone */ }
      browserFinishedRef.current = null;
    }
  }, []);

  // Success arrives via the App.tsx deep-link handler, which dispatches
  // `vyv:oauth-complete` the moment exchangeCodeForSession resolves — before it
  // navigates away. Tear down recovery immediately so the dismiss listener
  // (fired by Browser.close on the same success) can't flash a cancel toast.
  useEffect(() => {
    mountedRef.current = true;
    const onComplete = () => {
      clearOAuthRecovery();
      setOauthLoading(null);
    };
    window.addEventListener("vyv:oauth-complete", onComplete);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("vyv:oauth-complete", onComplete);
      clearOAuthRecovery();
    };
  }, [clearOAuthRecovery]);

  // Native fallback exchange. When Supabase returns the in-app browser to
  // capacitor://localhost/auth?code=... as an INTERNAL webview navigation
  // (iOS 18.7 / Capacitor 8) rather than the com.aivyv.mirror:// custom scheme,
  // App.tsx's appUrlOpen listener never fires. With detectSessionInUrl disabled
  // on native (so the SDK doesn't race a second single-use exchange), nothing
  // else would consume the code — this effect performs the one exchange for
  // that case. The shared exchangeNativeOAuthOnce dedupe guarantees we never
  // double-exchange with the deep-link path; the local ref guards StrictMode /
  // re-render double-invocation.
  const nativeRouteExchangeRanRef = useRef(false);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (nativeRouteExchangeRanRef.current) return;
    if (typeof window === "undefined") return;
    const code = new URLSearchParams(window.location.search).get("code");
    if (!code) return;
    nativeRouteExchangeRanRef.current = true;
    (async () => {
      const result = await exchangeNativeOAuthOnce(window.location.href);
      if (result.status === "error") {
        console.warn("[auth-route] native exchange failed:", result.error);
        Sentry.captureException(result.error, {
          tags: { feature: "oauth_deeplink_exchange" },
          contexts: { oauth: { step: "exchange_route" } },
        });
        toast.error(t("auth.error"));
        return;
      }
      if (result.status === "no_code") return;
      // ok | skipped → session is (being) established. Tear down OAuth recovery
      // and route through /auth/callback like the deep-link path does.
      window.dispatchEvent(new Event("vyv:oauth-complete"));
      navigate("/auth/callback", { replace: true });
    })();
  }, [navigate, t]);

  const handleOAuth = async (provider: "google" | "apple") => {
    // Social signup_started is NOT fired here anymore. Firing on the click lost
    // events to the redirect race / deep-link return / restored session (started
    // 14 << completed 18). It now fires in AuthCallback, atomically before
    // signup_completed, at the single point that sees 100% of social completions.
    // (Email still fires signup_started on form engagement — see the inputs.)
    //
    // oauth_started fires HERE, on the click, so the OAuth funnel has a
    // numerator even for users who never return with a session (the redirect
    // failures we can't otherwise see). It leaves the page via the existing
    // flushAnalytics() before window.location.assign on the web path. is_webview
    // flags an in-app social browser (web only — the native app's own WebView is
    // not an embedded browser, so it reports false).
    const isNative = Capacitor.isNativePlatform();
    analytics.oauthStarted(provider, {
      isNative,
      isWebview: isNative ? false : isInAppBrowser(),
    });
    setOauthLoading(provider);
    try {
      // ── NATIVE iOS / Android path ────────────────────────────────────────
      // signInWithOAuth in a Capacitor WKWebView redirects through the
      // system browser (external Safari) which (a) violates Apple Review
      // Guideline 4.8 for Apple Sign In and (b) breaks `window.location.origin`
      // (= "capacitor://localhost") as a Supabase redirect URL. Switch by
      // platform here. The web path below is unchanged.
      if (Capacitor.isNativePlatform()) {
        if (provider === "apple") {
          // ASAuthorizationController via @aparajita/capacitor-apple-sign-in.
          // Returns identityToken (JWT) → swap for a Supabase session via
          // signInWithIdToken. The Supabase Apple provider must accept the
          // app's Bundle ID as a client audience.
          const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
          const { rawNonce, hashedNonce } = await generateAppleNonce();
          const result: any = await SignInWithApple.authorize({
            clientId: NATIVE_BUNDLE_ID,
            // Required by the plugin types; on iOS the native
            // ASAuthorizationController flow does NOT redirect — Apple
            // ignores this value and returns the credential directly via
            // delegate callback. Kept consistent with the Google deep link.
            redirectURI: NATIVE_OAUTH_REDIRECT,
            scopes: "email name",
            nonce: hashedNonce,
          });
          const identityToken: string | undefined =
            result?.response?.identityToken ?? result?.identityToken;
          if (!identityToken) {
            throw new Error("apple_identity_token_missing");
          }
          // Apple returns givenName/familyName/email in the credential ONLY on
          // the FIRST authorization for this Apple ID — never again. The
          // identityToken JWT carries `email` but NOT the name, so the name
          // must be read here from the response object before it's lost. We
          // persist it right after the session exists (below) so onboarding can
          // pre-fill it and skip forcing the user to retype it (Guideline 4.0.0).
          const appleResp = result?.response ?? result ?? {};
          const appleFullName = [appleResp.givenName, appleResp.familyName]
            .filter((p: unknown): p is string => typeof p === "string" && p.length > 0)
            .join(" ")
            .trim();
          const { error } = await supabase.auth.signInWithIdToken({
            provider: "apple",
            token: identityToken,
            nonce: rawNonce,
          });
          if (error) throw error;
          // Stash the Apple-provided name into user_metadata.full_name, but only
          // if it isn't already set — Apple sends it once, so a re-login (where
          // the credential has no name) must not clobber a previously captured
          // value. Google already populates full_name via its idToken. Best
          // effort: a failure here must never block sign-in.
          if (appleFullName) {
            try {
              const { data: { user } } = await supabase.auth.getUser();
              const existing = (user?.user_metadata as { full_name?: string } | undefined)?.full_name;
              if (!existing) {
                await supabase.auth.updateUser({ data: { full_name: appleFullName } });
              }
            } catch (metaErr) {
              console.error("[auth] apple full_name persist failed:", metaErr);
            }
          }
          // Session is now stored; route through /auth/callback so consent
          // gating + profile→/home routing run exactly like the web flow.
          navigate("/auth/callback", { replace: true });
          return;
        }

        // Google via NATIVE Sign-In (GIDSignIn through @capgo/social-login),
        // mirroring the Apple path above: get a Google idToken natively, then
        // swap it for a Supabase session with signInWithIdToken. This removes
        // the in-app browser, custom-scheme redirect, PKCE and flow state that
        // made the OAuth-browser flow fail on iOS (Sentry VYV-FRONTEND-S). The
        // Supabase Google provider must list the iOS client ID under Authorized
        // Client IDs so it accepts the native idToken's audience.
        const { SocialLogin } = await import("@capgo/capacitor-social-login");
        // initialize is idempotent — safe to call before every login.
        await SocialLogin.initialize({
          google: {
            iOSClientId: GOOGLE_IOS_CLIENT_ID,
            webClientId: GOOGLE_WEB_CLIENT_ID,
            mode: "online",
          },
        });
        // Nonce alignment — fixes Sentry feature=google_native_idtoken
        // "Passed nonce and nonce in id_token should either both exist or not"
        // (Google login failing on iPhone, prod 01/06). GIDSignIn embeds a nonce
        // claim in the Google id_token, so signInWithIdToken MUST receive the
        // matching nonce. The @capgo response does NOT expose the nonce it used
        // (GoogleProvider.swift createOnlineLoginResponse omits it), so we supply
        // our own deterministic one and mirror the Apple flow + Supabase's
        // documented native-Google pattern: pass the SHA-256 HASH to the provider
        // (GIDSignIn reflects it verbatim into the id_token nonce claim) and the
        // RAW nonce to Supabase (it SHA-256s the raw and compares to the claim).
        // generateAppleNonce is a generic SHA-256 nonce helper — reused here; the
        // Apple flow above is untouched.
        const { rawNonce, hashedNonce } = await generateAppleNonce();
        // NO `scopes` here (fix 12/07, Sentry "You CANNOT use scopes without
        // modifying the main activity"): on Android the @capgo plugin rejects
        // ANY scopes array unless MainActivity implements
        // ModifiedMainActivityForSocialLoginPlugin — even a redundant one.
        // Both platforms already grant exactly what we were passing by
        // default: Android GoogleProvider.java always adds userinfo.email +
        // userinfo.profile + openid; iOS defaultGrantedScopes = ["email",
        // "profile", "openid"]. Omitting the array keeps identical grants
        // and skips the guard.
        const res: any = await SocialLogin.login({
          provider: "google",
          options: { nonce: hashedNonce },
        });
        const idToken: string | undefined = res?.result?.idToken;
        if (!idToken) throw new Error("google_idtoken_missing");
        const { error } = await supabase.auth.signInWithIdToken({
          provider: "google",
          token: idToken,
          nonce: rawNonce,
        });
        if (error) throw error;
        // Session is now stored; route through /auth/callback so consent
        // gating + profile→/home routing run exactly like the web flow.
        navigate("/auth/callback", { replace: true });
        return;
      }

      // ── WEB path ─────────────────────────────────────────────────────────
      // Take control of the redirect (skipBrowserRedirect) instead of letting
      // Supabase navigate synchronously the moment signInWithOAuth resolves:
      // that instant redirect aborted the in-flight signup_started request
      // (prod: google started 11 << completed 24). We flush analytics, THEN
      // navigate manually, so the event always leaves the page first.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          skipBrowserRedirect: true,
        },
      });
      if (error) {
        const msg = (error.message ?? "").toLowerCase();
        if (msg.includes("beta_closed") || msg.includes("beta whitelist")) {
          toast.error(t("auth.beta.closed_message"));
        } else {
          toast.error(error.message);
        }
        setOauthLoading(null);
        return;
      }
      // Success → flush any pending analytics before the redirect unmounts the
      // component, then hand off to the provider. (signup_started no longer fires
      // here — see handleOAuth top — but keep the flush so any other queued event
      // leaves the page first.)
      if (data?.url) {
        await flushAnalytics();
        window.location.assign(data.url);
      }
    } catch (err: any) {
      clearOAuthRecovery();
      const msg = (err?.message ?? "").toString();
      const lower = msg.toLowerCase();
      const isCancel =
        lower.includes("canceled") || lower.includes("cancelled") || lower.includes("user_cancel");
      if (lower.includes("beta_closed") || lower.includes("beta whitelist")) {
        toast.error(t("auth.beta.closed_message"));
      } else if (isCancel) {
        // Apple Sign In sheet dismissed — silent abort, no toast.
      } else {
        toast.error(msg || t("auth.error"));
      }
      // Capture the REAL underlying error (e.g. SignInWithApple.authorize()
      // failing before Supabase is ever called — entitlement / Service ID
      // drift). User cancellations are expected, so they're not reported.
      if (!isCancel) {
        const isNative = Capacitor.isNativePlatform();
        // Native Google now uses the GIDSignIn → idToken path; tag it distinctly
        // so its failures are diagnosable apart from the Apple native flow.
        const feature =
          isNative && provider === "google" ? "google_native_idtoken" : "oauth_native";
        Sentry.captureException(err, {
          tags: { feature, provider },
          contexts: { oauth: { provider, native: isNative } },
        });
      }
      setOauthLoading(null);
    }
  };

  // Never carry consent across a signup↔login toggle — a login flow does not
  // ask for consent, so the prior signup's checkbox state is meaningless.
  useEffect(() => {
    setConsentAccepted(false);
  }, [isSignUp]);

  const openForgot = () => {
    setForgotEmail(email);
    setForgotSent(false);
    setForgotOpen(true);
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = forgotEmail.trim();
    if (!trimmed) {
      toast.error(t("auth.forgot.error.required"));
      return;
    }
    setForgotLoading(true);
    try {
      // redirectTo MUST be on the Supabase Auth allowlist (Authentication →
      // URL Configuration → Redirect URLs). Production canonical:
      // https://www.aivyv.com/reset-password.
      const redirectTo =
        typeof window !== "undefined"
          ? window.location.origin + "/reset-password"
          : "https://www.aivyv.com/reset-password";
      const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo,
      });
      if (error) throw error;
      setForgotSent(true);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? "";
      console.warn("[Auth] resetPasswordForEmail failed:", err);
      toast.error(msg || t("auth.forgot.error.generic"));
      try {
        Sentry.captureException(err instanceof Error ? err : new Error(msg || "forgot password failed"), {
          tags: { feature: "auth_forgot_password_failed" },
        });
      } catch { /* Sentry not initialized in dev */ }
    } finally {
      setForgotLoading(false);
    }
  };

  const submitDisabled = loading || (isSignUp && !consentAccepted);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSignUp && !consentAccepted) {
      toast.error(t("auth.consent.required"));
      return;
    }
    setLoading(true);
    try {
      if (isSignUp) {
        // signup_started fires earlier, on first form engagement (see
        // markSignupStarted) — not here at submit, so the form-fill drop-off
        // stays measurable. Submit only records the completion below.
        // Atribuição de aquisição pelo caminho ATÔMICO: o primeiro toque entra
        // em raw_user_meta_data na própria transação do cadastro, e o trigger
        // trg_capture_signup_attribution copia para signup_attribution sem
        // depender de nenhuma chamada posterior do cliente. `undefined` quando
        // não há primeiro toque — o signUp segue exatamente como antes.
        // `emailRedirectTo` REUSA o corredor do OAuth. Com "Confirm email"
        // ligado o link volta com `?code=` — a mesma forma que o appUrlOpen de
        // App.tsx:196 já filtra e que o /auth/callback já sabe finalizar. Um
        // esquema novo seria um segundo listener e um segundo lugar para
        // divergir. Inofensivo com o toggle desligado: sem e-mail de
        // confirmação, ninguém usa este destino.
        const signUpResult = await supabase.auth.signUp({
          email,
          password,
          options: {
            // platform ("ios" | "android" | "web") e app_version (SHA do
            // bundle, o mesmo do app_opened) entram no raw_user_meta_data:
            // "quantos cadastros vieram do nativo" vira um SELECT no banco.
            // O PostHog não pode ser essa régua — o bundle nativo não emite
            // nada para ele (inventário de $host em 14d: nenhum localhost).
            // O trigger de atribuição lê só a chave vyv_attribution; chaves
            // irmãs não o tocam.
            data: {
              ...signupMetadata(),
              platform: Capacitor.getPlatform(),
              app_version: APP_VERSION,
            },
            emailRedirectTo: authCallbackRedirectTo({
              native: Capacitor.isNativePlatform(),
              origin: typeof window !== "undefined" ? window.location.origin : null,
            }),
          },
        });
        const { data, error } = signUpResult;
        // A decisão sai de uma função pura e testada — ver src/lib/
        // emailConfirmation.ts. O que ela protege: com proteção de enumeração
        // ligada, uma conta JÁ CONFIRMADA responde com sucesso falso
        // (error null, session null, identities []), que é indistinguível de
        // "cadastro novo aguardando confirmação" se olhado só por "tem user e
        // não tem sessão". A ordem do teste é o que impede essa conta de cair
        // na tela de confirmação (exigência nº 6).
        const outcome = decideSignupOutcome(signUpResult);

        // ── Existing-account dedupe (Sentry VYV-FRONTEND-2M / 2F) ──────────
        // Returning users (typically Instagram's in-app browser, where the
        // localStorage session doesn't survive) land on the default SIGNUP
        // mode and resubmit an email that already has an account. Supabase
        // reports that as AuthApiError code `user_already_exists` (422; the
        // message-regex fallback covers shapes without a code) — or, with
        // email enumeration protection enabled, as a fake success whose user
        // carries an empty identities[]. Both mean this is a LOGIN.
        const isExistingAccount = outcome.kind === "existing_account";
        if (isExistingAccount) {
          // LOGIN path, never a signup: signup_completed and the Meta
          // CompleteRegistration mirror (pixel + CAPI) must NOT fire here,
          // or returning users would pollute the campaign-optimization event.
          const isWebview = isInAppBrowser();
          const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
          if (!loginError) {
            analytics.signupDedupeToLogin("auto_login_success", { isWebview });
            splash.show();
            navigate("/mirror");
            return;
          }
          // Wrong password — or an account created via Google/Apple, which has
          // none. Flip to sign-in mode keeping the typed email; the inline
          // notice + the visible forgot-password link take it from there.
          analytics.signupDedupeToLogin("fallback_to_login", { isWebview });
          setIsSignUp(false);
          setDedupeNotice(true);
          return;
        }
        if (outcome.kind === "failed") {
          throw error ?? new Error("signup returned neither user nor session");
        }
        analytics.signupCompleted("email");
        // Meta mirror (browser + CAPI) + Advanced Matching with the
        // just-signed-up email. Fire-and-forget: ads tracking never blocks
        // signup. The event_id is DETERMINISTIC (signup-<user_id>) so the
        // SERVER event emitted from the auth.users row by
        // meta-capi-signup-dispatch dedupes against both browser paths —
        // without it, every signup where fbq DID load would count twice.
        // Fallback to a random id only when user is somehow absent (the
        // server pair still fires; dedupe is what's lost, not the count).
        const signupUserId = data.user?.id;
        const metaEventId = metaTrack(
          "CompleteRegistration",
          { method: "email" },
          signupUserId ? signupEventId(signupUserId) : undefined,
        );
        void metaIdentify(email);
        void sendMetaCapi("CompleteRegistration", metaEventId, { method: "email" }, email);

        // TODO (v1.1): move this insert into a server-side trigger or edge
        // function so the consent row is created atomically with auth.users
        // and captures a trusted IP hash. Today it runs client-side, which
        // means a flaky network here leaves the user account without a
        // consent row. We log + continue rather than block the signup UX.
        // ── SESSÃO: o que muda quando o toggle liga ────────────────────────
        // Com "Confirm email" ligado o signUp devolve `session: null`. Tudo
        // daqui para baixo escrevia com a sessão do próprio usuário e, sem
        // ela, o RLS recusa — sem lançar, só um console.error. Era a falha
        // silenciosa da exigência nº 2, em dois lugares:
        //
        //   · user_consents  — o insert vira 0 linhas. Quem recolhe o
        //     consentimento depois é o modal que o /auth/callback JÁ tem
        //     (AuthCallback.tsx:220): ele checa `hasPrivacyConsent` e abre o
        //     bloqueador quando falta. Então o consentimento não se perde,
        //     muda de momento — passa a ser pedido depois de confirmar.
        //   · claimReferral — precisa de sessão. O código fica guardado
        //     (só limpamos em "ok") e é reivindicado no /auth/callback,
        //     depois da confirmação. Sem isso todo indicado por e-mail
        //     sumiria calado.
        const hasSession = outcome.kind === "session";

        const newUserId = data.user?.id;
        if (hasSession && newUserId) {
          // Cast to any: user_consents is absent from the generated types
          // file (regenerated after the migration applies in prod). Pattern
          // matches other not-yet-typed tables across the app (e.g.
          // language_sessions in LanguageLessonView.tsx).
          const { error: consentErr } = await (supabase as any)
            .from("user_consents")
            .insert({
              user_id: newUserId,
              consent_type: "privacy_policy",
              policy_version: PRIVACY_POLICY_VERSION,
              // Client cannot securely derive its public IP; leaving null.
              ip_hash: null,
              user_agent: navigator.userAgent.slice(0, 500),
            });
          if (consentErr) {
            console.error("[CONSENT_LOG_FAILED]", consentErr);
          }
        } else if (!newUserId) {
          console.error("[CONSENT_LOG_FAILED] signUp returned no user id");
        }

        // Referral attribution — best-effort, never blocks signup. Records the
        // (referrer → this user) link; the R$5/R$5 credit grant is a separate
        // server-side step gated on the first qualifying payment.
        const refCode = normalizeReferralCode(referralCode);
        if (hasSession && newUserId && refCode) {
          const result = await claimReferral(refCode);
          if (result === "ok") clearStoredReferralCode();
          else if (result === "self") toast.error(t("referral.error.self"));
          else if (result === "not_found") toast.error(t("referral.error.notFound"));
          else if (result === "cap_reached") toast.error(t("referral.error.capReached"));
          // 'already_referred' / 'error' → silent (don't disrupt onboarding).
        }

        // A BIFURCAÇÃO. Antes daqui havia uma linha só —
        // `navigate("/onboarding/identity")` — e ela assumia sessão: as rotas
        // de onboarding não são protegidas (App.tsx:500 renderiza
        // OnboardingIdentity direto), então sem sessão o usuário entrava numa
        // tela que parece certa e cujas escritas o RLS recusa uma a uma.
        if (!hasSession) {
          writePendingConfirmation({
            email,
            resendableAt: Date.now() + RESEND_COOLDOWN_MS,
          });
          // Funil da mitigação (task 1b643867): "e-mail enviado" com a
          // plataforma explícita — o PostHog não enxerga o WebView nativo
          // por $host, então a propriedade é o único recorte de plataforma.
          analytics.signupEmailConfirmationPending(appPlatform());
          navigate("/auth/confirm-email", { state: { email } });
          return;
        }
        toast.success(t("auth.checkEmail"));
        navigate("/onboarding/identity");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        // Cover the Auth → Espelho handoff with the branded splash. The Espelho
        // (MirrorChat) now hides it on mount (voice-first entry); the context's
        // 4s safety timeout still prevents a stuck splash if something fails.
        splash.show();
        navigate("/mirror");
      }
    } catch (err: any) {
      const msg = (err?.message ?? "").toString();
      const lower = msg.toLowerCase();
      console.error("[Auth] email submit failed:", err);
      if (lower.includes("beta_closed") || lower.includes("beta whitelist")) {
        toast.error(t("auth.beta.closed_message"));
      } else {
        toast.error(msg || t("auth.error"));
      }
      // Expected credential mistakes (wrong password etc.) stay console-only;
      // everything else reaches Sentry so an /auth regression is visible.
      if (!lower.includes("invalid login credentials") && !lower.includes("beta")) {
        try {
          Sentry.captureException(err, {
            tags: { feature: "auth_email_submit_failed", mode: isSignUp ? "signup" : "signin" },
          });
        } catch { /* Sentry not initialized in dev */ }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileLayout className="justify-center py-12 relative">
      <button onClick={() => navigate("/welcome")}
        type="button" aria-label={t("common.back")}
        className="absolute top-[calc(env(safe-area-inset-top,0px)+1rem)] left-3 flex h-11 w-11 items-center justify-center rounded-xl text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors">
        <ArrowLeft size={20} />
      </button>

      <motion.div className="flex flex-col items-center gap-10 w-full"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="text-center space-y-2">
          <h2 className="font-display text-2xl font-semibold text-foreground">
            {isSignUp ? t("auth.createMirror") : t("auth.welcomeBack")}
          </h2>
          <p className="text-muted-foreground text-sm">
            {isSignUp ? t("auth.signUpSub") : t("auth.signInSub")}
          </p>
        </div>

        {/* OAuth providers — above the email/password form. Works in
            BETA_WHITELIST_MODE: the enforce_beta_whitelist trigger blocks
            non-allowlisted users with the same `beta_closed:` SQLSTATE that
            email/password signup already translates in the catch above. */}
        <div className="w-full space-y-3">
          {dedupeNotice && !isSignUp && (
            <div
              role="status"
              className="rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-[13px] leading-relaxed text-foreground/90"
            >
              {t("auth.dedupe.existingAccount")}
            </div>
          )}
          <button
            type="button"
            onClick={() => handleOAuth("google")}
            disabled={oauthLoading !== null}
            aria-busy={oauthLoading === "google"}
            className="w-full h-12 rounded-xl border border-border/50 bg-secondary/40 hover:bg-secondary/60 hover:ring-1 hover:ring-cyan-400/30 text-foreground text-sm font-medium flex items-center justify-center gap-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {oauthLoading === "google" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.859-3.048.859-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
              </svg>
            )}
            <span>{t("auth.oauth.continueWithGoogle")}</span>
          </button>

          {/* Apple Sign In is iOS-only on native: the plugin's authorize()
              throws "not implemented on android" (Sentry 12/07). A visible
              broken button is worse than none — hide it on native Android.
              Web (any OS) keeps it: the web path uses Supabase OAuth. */}
          {Capacitor.getPlatform() !== "android" && (
            <button
              type="button"
              onClick={() => handleOAuth("apple")}
              disabled={oauthLoading !== null}
              aria-busy={oauthLoading === "apple"}
              className="w-full h-12 rounded-xl border border-border/50 bg-secondary/40 hover:bg-secondary/60 hover:ring-1 hover:ring-cyan-400/30 text-foreground text-sm font-medium flex items-center justify-center gap-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {oauthLoading === "apple" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Apple size={18} className="fill-foreground" />
              )}
              <span>{t("auth.oauth.continueWithApple")}</span>
            </button>
          )}

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-border/40" />
            <span className="text-muted-foreground/60 text-[11px] uppercase tracking-widest">
              {t("auth.oauth.divider")}
            </span>
            <div className="flex-1 h-px bg-border/40" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="w-full space-y-3">
          <label htmlFor="auth-email" className="block text-sm font-medium text-foreground">{t("auth.email")}</label>
          <Input id="auth-email" type="email" autoComplete="email" placeholder={t("auth.email")} value={email}
            onChange={(e) => { setEmail(e.target.value); if (isSignUp) markSignupStarted("email"); }}
            className="bg-secondary/60 border-border/50 text-foreground placeholder:text-muted-foreground h-13 rounded-xl text-sm" required />
          <label htmlFor="auth-password" className="block text-sm font-medium text-foreground">{t("auth.password")}</label>
          <Input id="auth-password" type="password" autoComplete={isSignUp ? "new-password" : "current-password"} placeholder={t("auth.password")} value={password}
            onChange={(e) => { setPassword(e.target.value); if (isSignUp) markSignupStarted("email"); }}
            className="bg-secondary/60 border-border/50 text-foreground placeholder:text-muted-foreground h-13 rounded-xl text-sm" required minLength={6} />

          {isSignUp && (
            <Input
              aria-label={t("auth.referralCode")}
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              placeholder={t("auth.referralCode")}
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="bg-secondary/60 border-border/50 text-foreground placeholder:text-muted-foreground h-13 rounded-xl text-sm tracking-[0.18em]"
              maxLength={12}
            />
          )}

          {!isSignUp && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={openForgot}
                className="text-muted-foreground text-[12px] hover:text-primary transition-colors"
              >
                {t("auth.forgot.link")}
              </button>
            </div>
          )}

          {isSignUp && (
            <label
              htmlFor="signup-consent"
              className="flex items-start gap-2.5 pt-1 cursor-pointer select-none"
            >
              <input
                id="signup-consent"
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border/60 bg-secondary/60 accent-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1 focus:ring-offset-transparent"
              />
              <span className="text-muted-foreground text-[12px] leading-relaxed">
                {t("auth.consent.checkboxLabelPrefix")}
                <Link
                  to="/privacy"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary hover:underline focus:outline-none focus:underline"
                >
                  {t("auth.consent.privacyPolicy")}
                </Link>
                {t("auth.consent.andConnector")}
                <Link
                  to="/terms"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary hover:underline focus:outline-none focus:underline"
                >
                  {t("auth.consent.termsOfService")}
                </Link>
              </span>
            </label>
          )}

          <div className="pt-2">
            <Button
              variant="hero"
              size="xl"
              className={`w-full ${submitDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
              type="submit"
              disabled={submitDisabled}
              aria-disabled={submitDisabled}
              title={
                isSignUp && !consentAccepted ? t("auth.consent.required") : undefined
              }
            >
              {loading ? t("common.loading") : isSignUp ? t("auth.createAccount") : t("auth.signIn")}
            </Button>
            {isSignUp && !consentAccepted && !loading && (
              <p
                id="signup-consent-hint"
                className="text-muted-foreground/60 text-[11px] mt-2 text-center"
                role="status"
              >
                {t("auth.consent.required")}
              </p>
            )}
          </div>
        </form>

        {BETA_WHITELIST_MODE ? (
          <p className="text-muted-foreground/60 text-[12px] text-center max-w-[260px] leading-relaxed">
            {t("auth.beta.closed_subtitle")}
          </p>
        ) : (
          <button onClick={() => { setIsSignUp(!isSignUp); setDedupeNotice(false); signupStartedProvidersRef.current.clear(); }}
            className="text-muted-foreground text-sm hover:text-primary transition-colors">
            {isSignUp ? t("auth.switchToSignIn") : t("auth.switchToSignUp")}
          </button>
        )}
      </motion.div>
      <Footer variant="minimal" />

      {onShowIntro && (
        <button
          type="button"
          onClick={onShowIntro}
          className="fixed bottom-6 right-6 z-20 flex items-center gap-1 text-white/40 hover:text-white text-xs transition-colors"
          aria-label={t("login.watch_intro")}
        >
          <PlayCircle size={14} />
          {t("login.watch_intro")}
        </button>
      )}

      {forgotOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
          onClick={() => !forgotLoading && setForgotOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl p-5 relative"
            style={{
              background: "hsl(220 40% 10%)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <button
              type="button"
              onClick={() => !forgotLoading && setForgotOpen(false)}
              aria-label={t("auth.forgot.close")}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-foreground/60 hover:text-foreground transition-colors disabled:opacity-40"
              disabled={forgotLoading}
            >
              <X size={16} />
            </button>

            {forgotSent ? (
              <div className="py-2">
                <h3 className="font-display text-[18px] font-semibold text-foreground mb-2">
                  {t("auth.forgot.sent.title")}
                </h3>
                <p className="text-[13px] font-body text-foreground/70 leading-relaxed mb-4">
                  {tf("auth.forgot.sent.body", { email: forgotEmail.trim() })}
                </p>
                <Button
                  variant="hero"
                  size="lg"
                  className="w-full"
                  onClick={() => setForgotOpen(false)}
                >
                  {t("auth.forgot.sent.close")}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="py-2">
                <h3 className="font-display text-[18px] font-semibold text-foreground mb-2">
                  {t("auth.forgot.title")}
                </h3>
                <p className="text-[13px] font-body text-foreground/70 leading-relaxed mb-4">
                  {t("auth.forgot.body")}
                </p>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder={t("auth.email")}
                  required
                  className="bg-secondary/60 border-border/50 text-foreground placeholder:text-muted-foreground h-12 rounded-xl text-sm mb-3"
                />
                <Button
                  variant="hero"
                  size="lg"
                  type="submit"
                  className="w-full"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t("auth.forgot.submit")
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </MobileLayout>
  );
};

export default Auth;
